import React, { useState, useEffect } from 'react';
import { collection, addDoc, updateDoc, doc, deleteDoc, onSnapshot, query, orderBy } from 'firebase/firestore';
import { db, handleFirestoreError, OperationType } from '../lib/firebase';
import { SurveyLink } from '../types';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Link as LinkIcon } from 'lucide-react';

export default function SurveyManager() {
  const [surveys, setSurveys] = useState<SurveyLink[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  
  const [formData, setFormData] = useState<{
    title: string;
    description: string;
    url: string;
    status: 'active' | 'archived';
    openDate?: string;
    closeDate?: string;
  }>({
    title: '',
    description: '',
    url: '',
    status: 'active',
    openDate: '',
    closeDate: ''
  });

  useEffect(() => {
    const q = query(collection(db, 'surveys'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      setSurveys(snap.docs.map(d => ({ id: d.id, ...d.data() } as SurveyLink)));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'surveys');
    });
    return () => unsub();
  }, []);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        openDate: formData.openDate || undefined,
        closeDate: formData.closeDate || undefined,
        createdAt: editingId ? undefined : new Date().toISOString()
      };
      
      // Remove undefined fields to satisfy Firestore
      if (!editingId) {
        await addDoc(collection(db, 'surveys'), JSON.parse(JSON.stringify(payload)));
        toast.success('Survey created successfully');
      } else {
        const { createdAt, ...updatePayload } = payload;
        await updateDoc(doc(db, 'surveys', editingId), JSON.parse(JSON.stringify(updatePayload)));
        toast.success('Survey updated successfully');
      }

      setIsEditing(false);
      setEditingId(null);
      setFormData({ title: '', description: '', url: '', status: 'active', openDate: '', closeDate: '' });
    } catch (err) {
      handleFirestoreError(err, OperationType.WRITE, 'surveys');
    }
  };

  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to permanently delete this survey link?')) {
      try {
        await deleteDoc(doc(db, 'surveys', id));
        toast.success('Survey deleted');
      } catch (err) {
        handleFirestoreError(err, OperationType.DELETE, 'surveys');
      }
    }
  };

  const handleEdit = (survey: SurveyLink) => {
    setFormData({
      title: survey.title,
      description: survey.description,
      url: survey.url,
      status: survey.status,
      openDate: survey.openDate || '',
      closeDate: survey.closeDate || ''
    });
    setEditingId(survey.id!);
    setIsEditing(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-lcars-blue uppercase tracking-widest">Survey & Form Manager</h2>
        {!isEditing && (
          <Button onClick={() => setIsEditing(true)} className="bg-lcars-gold hover:bg-white text-black font-bold uppercase">
            <Plus className="w-4 h-4 mr-2" /> NEW SURVEY LINK
          </Button>
        )}
      </div>

      {isEditing && (
        <form onSubmit={handleSave} className="bg-white/5 border border-lcars-blue p-6 rounded-xl space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label htmlFor="survey-title" className="text-[0.6rem] text-lcars-blue font-bold uppercase tracking-widest">Survey Title</label>
              <Input 
                id="survey-title"
                name="surveyTitle"
                required
                value={formData.title}
                onChange={e => setFormData({...formData, title: e.target.value})}
                className="bg-[var(--background)] border-lcars-blue text-[var(--foreground)]"
                placeholder="e.g. 2026 Fleet Feedback"
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="survey-url" className="text-[0.6rem] text-lcars-blue font-bold uppercase tracking-widest">Jotform URL</label>
              <Input 
                id="survey-url"
                name="surveyUrl"
                required
                type="url"
                value={formData.url}
                onChange={e => setFormData({...formData, url: e.target.value})}
                className="bg-[var(--background)] border-lcars-blue text-[var(--foreground)]"
                placeholder="https://form.jotform.com/..."
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label htmlFor="survey-open" className="text-[0.6rem] text-lcars-blue font-bold uppercase tracking-widest">Open Date (Optional)</label>
              <Input 
                id="survey-open"
                name="surveyOpen"
                type="datetime-local"
                value={formData.openDate || ''}
                onChange={e => setFormData({...formData, openDate: e.target.value})}
                className="bg-[var(--background)] border-lcars-blue text-[var(--foreground)]"
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="survey-close" className="text-[0.6rem] text-lcars-blue font-bold uppercase tracking-widest">Close Date (Optional)</label>
              <Input 
                id="survey-close"
                name="surveyClose"
                type="datetime-local"
                value={formData.closeDate || ''}
                onChange={e => setFormData({...formData, closeDate: e.target.value})}
                className="bg-[var(--background)] border-lcars-blue text-[var(--foreground)]"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <label htmlFor="survey-desc" className="text-[0.6rem] text-lcars-blue font-bold uppercase tracking-widest">Description</label>
            <textarea 
              id="survey-desc"
              name="surveyDesc"
              required
              value={formData.description}
              onChange={e => setFormData({...formData, description: e.target.value})}
              className="w-full bg-[var(--background)] border border-lcars-blue text-[var(--foreground)] rounded p-2 text-sm outline-none"
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="survey-status" className="text-[0.6rem] text-lcars-blue font-bold uppercase tracking-widest">Status</label>
            <select 
              id="survey-status"
              name="surveyStatus"
              value={formData.status}
              onChange={e => setFormData({...formData, status: e.target.value as 'active' | 'archived'})}
              className="h-10 w-full rounded border border-lcars-blue bg-[var(--background)] px-3 py-2 text-sm text-[var(--foreground)] outline-none uppercase font-bold"
            >
              <option value="active">Active (Visible to Crew)</option>
              <option value="archived">Hidden / Draft (Hidden from Crew)</option>
            </select>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button 
              type="button" 
              variant="outline"
              onClick={() => {
                setIsEditing(false);
                setEditingId(null);
                setFormData({ title: '', description: '', url: '', status: 'active', openDate: '', closeDate: '' });
              }}
              className="border-white/20 text-[var(--foreground)] hover:bg-white/10"
            >
              CANCEL
            </Button>
            <button type="submit" className="px-4 py-2 rounded-lg bg-lcars-blue text-black font-bold outline-none hover:bg-lcars-blue/80 transition-colors">
              {editingId ? 'SAVE CHANGES' : 'CREATE SURVEY'}
            </button>
          </div>
        </form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {surveys.length === 0 && !isEditing && (
          <div className="col-span-full p-8 text-center text-[var(--foreground)]/40 italic">
            No surveys found in the database.
          </div>
        )}
        
        {surveys.map(survey => (
          <div key={survey.id} className={`p-4 rounded-xl border flex flex-col justify-between ${survey.status === 'active' ? 'bg-lcars-blue/10 border-lcars-blue' : 'bg-[var(--background)] border-white/10 opacity-70'}`}>
            <div>
              <div className="flex justify-between items-start mb-2">
                <h3 className="text-lg font-bold text-lcars-gold truncate">{survey.title}</h3>
                <span className={`text-[0.6rem] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${survey.status === 'active' ? 'bg-lcars-blue text-black' : 'bg-white/20 text-[var(--foreground)]'}`}>
                  {survey.status === 'active' ? 'Active' : 'Hidden'}
                </span>
              </div>
              {(survey.openDate || survey.closeDate) && (
                <div className="flex gap-4 mb-2 text-xs font-mono text-lcars-blue/70 bg-lcars-blue/10 p-2 rounded">
                  {survey.openDate && <span>Opens: {new Date(survey.openDate).toLocaleString()}</span>}
                  {survey.closeDate && <span>Closes: {new Date(survey.closeDate).toLocaleString()}</span>}
                </div>
              )}
              <p className="text-sm text-[var(--foreground)]/80 line-clamp-2 mb-4">{survey.description}</p>
            </div>
            
            <div className="flex justify-between items-center mt-4">
              <a href={survey.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-lcars-blue hover:text-[var(--foreground)] transition-colors">
                <LinkIcon className="w-3 h-3" /> TEST LINK
              </a>
              <div className="flex gap-2">
                <button onClick={() => handleEdit(survey)} className="p-2 text-[var(--foreground)]/50 hover:text-lcars-gold transition-colors">
                  <Edit2 className="w-4 h-4" />
                </button>
                <button onClick={() => handleDelete(survey.id!)} className="p-2 text-[var(--foreground)]/50 hover:text-lcars-red transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
